import { useCallback, useState } from "react";

import { Colors, FormGroup } from "@blueprintjs/core";
import { Editor } from "@tiptap/react";

import { Box, Flex } from "@/components/layout/flexbox";
import { AudioPreview } from "@/components/pages_logged_in/roleplays/details/scenario/edit/AudioPreview";
import {
  action,
  state,
} from "@/components/pages_logged_in/roleplays/details/scenario/edit/useEditScenario";
import { Tiptap } from "@/components/pieces/editor/tiptap";
import { ExpandableTextInput } from "@/components/ui/expandable-text-input";
import { FileList } from "@/components/ui/file-list";
import { LIGHTEST_BACKGROUND, SUBTEXT_COLOR } from "@/css/constants";
import { uploadFile } from "@/lib/upload-file";

export function ScenarioTab({
  state,
  dispatch,
  onPreviewClick,
}: {
  state: state;
  dispatch: (action: action) => void;
  onPreviewClick?: () => Promise<void>;
}) {
  // Define available variables for this scenario
  const scenarioVariables = {
    first_name: state.persona.firstName || "First Name",
    last_name: state.persona.lastName || "Last Name",
    name: state.persona.name || "Name",
    company: state.persona.company || "Company",
    job_title: state.persona.jobTitle || "Job Title",
  };
  const handleContextChange = useCallback(
    (value: string) => {
      dispatch({ type: "setContext", context: value });
    },
    [dispatch],
  );

  const handleObjectiveChange = useCallback(
    (value: string) => {
      dispatch({ type: "setObjective", objective: value });
    },
    [dispatch],
  );

  const [editor, setEditor] = useState<Editor | null>(null);
  const handleIntroductionScriptClick = useCallback(() => {
    editor?.commands.focus();
  }, [editor]);

  return (
    <Flex
      flexDirection="column"
      p={24}
      pb={12}
      backgroundColor={LIGHTEST_BACKGROUND}
      className="flex-auto"
    >
      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Scenario Context
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            What learners see before the conversation begins
          </Box>
        }
      >
        <Flex mt={2} backgroundColor={Colors.WHITE} className="round-corners">
          <Tiptap
            content={state.context ?? ""}
            onChange={handleContextChange}
            onFileUpload={uploadFile({ isPrivate: false })}
            variables={scenarioVariables}
          />
        </Flex>
      </FormGroup>
      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Scenario Objective
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            Define what the learner should accomplish in this conversation and
            any outcomes they should avoid.
          </Box>
        }
      >
        <Flex mt={2} backgroundColor={Colors.WHITE} className="round-corners">
          <Tiptap
            content={state.objective ?? ""}
            onChange={handleObjectiveChange}
            onFileUpload={uploadFile({ isPrivate: false })}
            variables={scenarioVariables}
          />
        </Flex>
      </FormGroup>
      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Introduction Audio <span className="font-normal">(optional)</span>
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            This text will be converted to narrated audio that plays before the
            scenario begins. Clearly explain the user's role, relevant
            background context, and exactly how the conversation will start.
          </Box>
        }
      >
        <div
          className="mt-2 flex flex-col gap-2 rounded-sm border border-gray-300 bg-white p-3"
          onClick={handleIntroductionScriptClick}
        >
          <ExpandableTextInput
            placeholder="In this scenario, you will..."
            onEditorChange={setEditor}
            value={state.introductionScript ?? ""}
            onChange={(value) =>
              dispatch({
                type: "setIntroductionScript",
                introductionScript: value,
              })
            }
            mode="text"
          />
          <AudioPreview
            audioUrl={
              state.audioPreview === "loaded" ||
              state.audioPreview === "generated"
                ? state.introductionAudioUrl
                : undefined
            }
            onPreviewClick={onPreviewClick}
            autoPlay={state.audioPreview === "generated"}
            loading={state.audioPreview === "generating"}
            disabled={!state.introductionScript}
          />
        </div>
      </FormGroup>
      <FormGroup
        label={
          <Box fontSize={18} mb={1} fontWeight={600}>
            Presentation Materials
          </Box>
        }
        labelInfo={
          <Box fontSize={14} fontWeight={400} color={SUBTEXT_COLOR}>
            Optionally, upload PDF files that the participant can choose to
            present during the session. The AI will have access to the content
            of the slides as they are being presented.
          </Box>
        }
      >
        <FileList
          isPrivate
          files={state.presentations}
          acceptedFileTypes={["application/pdf"]}
          onAdd={(file) =>
            dispatch({ type: "addPresentationFile", payload: file })
          }
          onRemove={(fileId) =>
            dispatch({ type: "removePresentationFile", payload: { fileId } })
          }
          onSort={(files) =>
            dispatch({ type: "sortPresentationFiles", payload: files })
          }
        />
      </FormGroup>
    </Flex>
  );
}
